"use client";

import Link from "next/link";
import type {
  CBlogArticle,
  CBlogContent,
  CBlogItem,
} from "@/lib/api/cms-catalog";
import dynamic from "next/dynamic";
import { useT } from "@/lib/i18n/context";
import { cBlogTranslations, type CBlogTranslations } from "./CBlog.i18n";
import { ParagraphRenderer } from "@/components/cms/Paragraphs/ParagraphRenderer";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CBlog.module.css";

/* Biblioteka CMS Interakcje (komentarze + reakcje) - ładowana leniwie
   (ssr:false), montuje się i dociąga dane dopiero po wyrenderowaniu wpisu. */
const ArticleInteractions = dynamic(
  () =>
    import("@/components/cms/Interactions/ArticleInteractions").then(
      (m) => m.ArticleInteractions,
    ),
  { ssr: false },
);

/**
 * CBlog - visitor view komponentu `c_blog` (Blog).
 *
 * **Data source:** `C_BLOG_CONTROLLER::getApiContent()` zwraca:
 *   - `list` - paginowana lista wpisow (item: id, name, short_text,
 *     publication_date, count_view, picture, href)
 *   - `article` - pojedynczy wpis (meta + autor + kategorie) + akapity
 *     (`imp_paragraph`) renderowane przez `<ParagraphRenderer>`.
 *
 * Tresc wpisu = wylacznie akapity (c_blog nie renderuje juz akapitow server-side -
 * `getHtmlParagraphs` usuniety). Media (zdjecia/pliki) siedza w akapitach.
 *
 * **Security:** `short_text` to HTML z admin panelu (trusted) - OK dla
 * `dangerouslySetInnerHTML`.
 */
export function CBlog({ content }: CBlogProps) {
  const t = useT(cBlogTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "article" && content.article ? (
        <ArticleView
          article={content.article}
          listHref={content.list_href ?? null}
          t={t}
        />
      ) : (
        <ListView content={content} t={t} />
      )}
    </div>
  );
}

export type CBlogProps = {
  content: CBlogContent;
};

/* List view */

function ListView({ content, t }: { content: CBlogContent; t: CBlogTranslations }) {
  const items: CBlogItem[] = Array.isArray(content.items) ? content.items : [];
  const pagination = content.pagination;

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  return (
    <CatalogLoadMore<CBlogItem>
      initialItems={items}
      initialPage={pagination?.page ?? 1}
      totalPages={pagination?.total_pages ?? 1}
      totalCount={pagination?.total_count}
      getKey={(item) => item.id}
      extractItems={(c) => {
        const arr = (c as CBlogContent | null)?.items;
        return Array.isArray(arr) ? arr : [];
      }}
    >
      {(allItems) => (
        <ul className={styles.list}>
          {allItems.map((item) => (
            <BlogCard key={item.id} item={item} t={t} />
          ))}
        </ul>
      )}
    </CatalogLoadMore>
  );
}

function BlogCard({ item, t }: { item: CBlogItem; t: CBlogTranslations }) {
  const href = extractInternalHref(item.href);

  const cardInner = (
    <>
      <div className={styles.thumbWrap}>
        {item.picture ? (
          <img
            src={item.picture.thumb_src}
            alt={item.picture.alt || item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.thumb}
            loading="lazy"
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>
      <div className={styles.body}>
        {item.publication_date && (
          <time className={styles.date} dateTime={item.publication_date}>
            {t.formatDate(item.publication_date)}
          </time>
        )}
        <h2 className={styles.title}>{item.name}</h2>
        <div
          className={styles.short}
          dangerouslySetInnerHTML={{ __html: item.short_text }}
        />
        <span className={styles.more}>{t.readMore}</span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </a>
      )}
    </li>
  );
}

/* Article view */

function ArticleView({
  article,
  listHref,
  t,
}: {
  article: CBlogArticle;
  listHref: string | null;
  t: CBlogTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;

  return (
    <article className={styles.article}>
      {(backHref !== null || listHref) && (
        <div className={styles.articleBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.articleBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.articleBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      <header className={styles.articleHeader}>
        <h2 className={styles.articleTitle}>{article.name}</h2>
        <div className={styles.articleMeta}>
          {article.publication_date && (
            <time className={styles.articleDate} dateTime={article.publication_date}>
              {t.formatDate(article.publication_date)}
            </time>
          )}
          {article.author?.name && (
            <span className={styles.articleAuthor}>{t.byAuthor(article.author.name)}</span>
          )}
        </div>
        {article.categorys && article.categorys.length > 0 && (
          <ul className={styles.articleCategories}>
            {article.categorys.map((cat, i) => {
              const catHref = extractInternalHref(cat.href);
              return (
                <li key={`${cat.id_catalog}-${i}`} className={styles.articleCategory}>
                  {catHref !== null ? (
                    <Link href={catHref}>{cat.name}</Link>
                  ) : (
                    <a href={cat.href}>{cat.name}</a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </header>

      {article.picture && (
        <figure className={styles.articleMainImage}>
          <img
            src={article.picture.full_src}
            alt={article.picture.alt || article.name}
            width={article.picture.width ?? undefined}
            height={article.picture.height ?? undefined}
            loading="eager"
          />
        </figure>
      )}

      {article.short_text && (
        <div
          className={styles.articleLead}
          dangerouslySetInnerHTML={{ __html: article.short_text }}
        />
      )}

      {article.paragraphs && article.paragraphs.length > 0 && (
        <ParagraphRenderer paragraphs={article.paragraphs} />
      )}

      {/* Reakcje + komentarze (biblioteka CMS Interakcje) - lazy, pod tresca
          wpisu. Reakcje renderowane nad komentarzami. */}
      {article.interactions &&
        (article.interactions.reactions?.enabled ||
          article.interactions.comments?.enabled) && (
          <ArticleInteractions interactions={article.interactions} />
        )}
    </article>
  );
}
